/**
 * The typing dots on the person's side of the live thread (12 Oct, Devendra:
 * "a three-dot typing effect in the thread while listening, and then enter
 * the message when we have it"), one model for the phone and the web, pure so
 * `bun test` reads every rule.
 *
 * The person's line reaches the thread only once the server has judged it
 * said to OS (side talk never shows), which is a transcriber's partial and a
 * classifier's answer after their voice began. The dots say at once, from the
 * client's own ear, that they are being heard; they carry no words, so they
 * show for speech the server later finds was not for OS, and go:
 *
 * - **Start** when the ear hears the person's own speech (`LiveEarModel`'s
 *   sent `speaking`: never while muted, never OS's echo or speech heard over
 *   OS on half duplex) while live mode is Listening. Once per stretch of
 *   speech: dots that went while the person still speaks do not come back
 *   until they stop and start again.
 * - **Stay** through the gaps between words and while OS turns to Working on
 *   the line, so they never flicker.
 * - **Give way** to the person's line the moment any line of theirs newer than
 *   the dots arrives, partial or settled: the line is drawn where they stood.
 *   While that line is still being written (`transcribing`: their newest line
 *   is partial) they never show, though the ear falls and rises between its
 *   words: the words themselves are the feedback (12 Oct, Devendra).
 * - **Go** at once on Mute, Quiet, OS speaking, or anything but Listening and
 *   Working; `LIVE_TYPING_PASSED_MS` after the server says an utterance that
 *   ended after the speech stopped passed (`passed`: not for OS, unsure, no
 *   words); and `LIVE_TYPING_GIVE_UP_MS` after the speech stopped with nothing
 *   from the server (a cough or a door the transcriber never wrote).
 * - **Go, while the ear still hears**, after `LIVE_TYPING_NO_WORDS_MS` with no
 *   line of the person's: a loud room, a TV or a fan the ear keeps taking for
 *   a voice would otherwise hold them up for good (12 Oct, on the simulator
 *   in a loud room). They are spent until the ear falls.
 */
import type { LiveStateName } from './live-state';
import type { LiveRow } from './live-transcript';

/**
 * How long the dots stay after the server says an utterance passed. Not at
 * once: a person who says something to someone else and turns straight to OS
 * ("chai rakh do. OS, aaj ki sale?") makes one stretch of dots for two
 * utterances, and the first one's `passed` can land while the second's line
 * is still on its way (a name cue is judged on its first partial, about a
 * transcriber's beat, 0.5 s, behind the voice). 1.2 s covers that beat twice
 * over; longer would leave dots on side talk that has been judged.
 */
export const LIVE_TYPING_PASSED_MS = 1_200;

/**
 * How long the dots stay after the speech stopped when the server says
 * nothing of it: the transcriber settles a line 0.5–1 s after the voice falls,
 * and the classifier answers within `VERDICT_TIMEOUT_MS` (2.5 s) of being
 * asked, which since 12 Oct is mostly on the pause itself, so a line still
 * coming has come by then. Speech the transcriber never wrote (a cough, a
 * door, a fan the ear took for a voice) opens no utterance and gets no
 * `passed`: this is its end.
 */
export const LIVE_TYPING_GIVE_UP_MS = 3_500;

/**
 * How long the dots stay while the ear keeps hearing with no line of the
 * person's: an addressed line reaches the thread on its first judged partial,
 * a beat or two in (a name cue or the window, about 1 s; a classifier's
 * answer, 1–2 s), so 5 s of sound with no line is not words for OS yet.
 */
export const LIVE_TYPING_NO_WORDS_MS = 5_000;

export type LiveTyping = {
  /** The dots show. */
  readonly shown: boolean;
  /** The ear heard the person speaking at the last step. */
  readonly speaking: boolean;
  /** The dots went during the stretch of speech going on now: they do not come back until it stops. */
  readonly spent: boolean;
  /** When the speech stopped while the dots show; null while the person speaks. */
  readonly quietAt: number | null;
  /** When the dots began; null while they do not show. */
  readonly startedAt: number | null;
  /** The newest line of the person's when the dots began: a newer one is the line that takes their place. */
  readonly lineBefore: string | null;
};

export const NEW_LIVE_TYPING: LiveTyping = { shown: false, speaking: false, spent: false, quietAt: null, startedAt: null, lineBefore: null };

export type LiveTypingInput = {
  /** The client's clock, ms. */
  readonly now: number;
  /** The ear hears the person's own speech going up now (the last `vad` it sent said speaking). */
  readonly speaking: boolean;
  /** Live mode's state (`LiveLook.state`); null with no call. */
  readonly state: LiveStateName | null;
  /** The id of the newest line of the person's the transcript holds (said or typed); null for none. */
  readonly lastLine: string | null;
  /** When the server last said an utterance passed (`passed`), on the client's clock; null for never. */
  readonly passedAt: number | null;
  /** The person's newest line is still partial: its transcript is being written in the thread. */
  readonly transcribing?: boolean;
};

/** The states the dots may start in, and the ones they may stay in. */
const STARTS: readonly LiveStateName[] = ['listening'];
const STAYS: readonly LiveStateName[] = ['listening', 'working'];

/** The dots after one more look at the ear, the state and the transcript. */
export const liveTypingStep = (model: LiveTyping, input: LiveTypingInput): LiveTyping => {
  const { now, speaking, state, lastLine, passedAt, transcribing = false } = input;
  // Their line is being written: no dots, and none for the rest of this stretch of speech.
  if (transcribing) return { ...NEW_LIVE_TYPING, speaking, spent: speaking, lineBefore: lastLine };
  // A stretch of speech ends when the ear falls: the next one may draw dots again.
  const spent = speaking && model.spent;
  if (!model.shown) {
    if (speaking && !spent && state !== null && STARTS.includes(state)) return { shown: true, speaking, spent: false, quietAt: null, startedAt: now, lineBefore: lastLine };
    return { ...NEW_LIVE_TYPING, speaking, spent, lineBefore: lastLine };
  }
  // Gone while the person still speaks: spent until they stop.
  const hidden: LiveTyping = { shown: false, speaking, spent: speaking, quietAt: null, startedAt: null, lineBefore: lastLine };
  // The person's line came: it stands where the dots stood.
  if (lastLine !== model.lineBefore) return hidden;
  if (state === null || !STAYS.includes(state)) return hidden;
  if (speaking) {
    const started = model.startedAt ?? now;
    if (now - started >= LIVE_TYPING_NO_WORDS_MS) return hidden;
    return { ...model, speaking, quietAt: null, startedAt: started };
  }
  const quietAt = model.quietAt ?? now;
  const passed = passedAt !== null && passedAt >= quietAt && now - passedAt >= LIVE_TYPING_PASSED_MS;
  if (passed || now - quietAt >= LIVE_TYPING_GIVE_UP_MS) return hidden;
  return { ...model, speaking, quietAt };
};

/**
 * The newest line of the person's in a transcript's rows, said or typed, and
 * kept or not (a line the thread now keeps holds its place as a `kept` row,
 * so an older line kept while the dots show is not taken for a new one); null
 * for none.
 */
export const lastPersonLineOf = (rows: readonly LiveRow[]): string | null =>
  rows.findLast((row) => row.role === 'you' || (row.role === 'kept' && row.messageRole === 'user'))?.id ?? null;

/** Whether the person's newest line, said or typed, is still partial: its words are arriving in the thread. */
export const personLineWritingOf = (rows: readonly LiveRow[]): boolean => {
  const last = rows.findLast((row) => row.role === 'you' || (row.role === 'kept' && row.messageRole === 'user'));
  return last !== undefined && last.role === 'you' && last.partial;
};
